import { useState, useEffect } from 'react';
import { X, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function TransferUnitDialogSimple({ open, onClose, rental, onTransferred }) {
  const [units, setUnits] = useState([]);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [transferType, setTransferType] = useState('continue');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      fetchAvailableUnits();
    }
  }, [open]);

  const fetchAvailableUnits = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/units/available-for-transfer?current_rental_id=${rental.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const allAvailableUnits = Array.isArray(response.data) ? response.data : [];
      
      // Filter same type only
      const currentType = getCurrentType();
      const sameTypeUnits = allAvailableUnits.filter(unit => {
        const unitType = getUnitType(unit);
        return unitType === currentType;
      });
      
      setUnits(sameTypeUnits);
    } catch (error) {
      console.error('Error fetching units:', error);
      toast.error('Gagal mengambil data unit');
    }
  };

  const getCurrentType = () => {
    if (rental.ps_type) return rental.ps_type.toUpperCase();
    const name = rental.ps_unit_name?.toUpperCase() || '';
    if (name.includes('PS5')) return 'PS5';
    if (name.includes('PS4')) return 'PS4';
    if (name.includes('PS3')) return 'PS3';
    return 'UNKNOWN';
  };

  const getUnitType = (unit) => {
    if (unit.ps_type) return unit.ps_type.toUpperCase();
    const name = unit.name?.toUpperCase() || '';
    if (name.includes('PS5')) return 'PS5';
    if (name.includes('PS4')) return 'PS4';
    if (name.includes('PS3')) return 'PS3';
    return 'UNKNOWN';
  };

  const handleTransfer = async () => {
    if (!selectedUnit) {
      toast.error('Pilih unit tujuan');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const requestData = {
        new_unit_id: selectedUnit.id,
        continue_time: transferType === 'continue',
        change_mode: transferType === 'change_mode'
      };

      const response = await axios.post(
        `${API_URL}/api/rentals/${rental.id}/transfer-unit`,
        requestData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success(response.data.message);
        onTransferred(response.data.rental);
        onClose();
      }
    } catch (error) {
      console.error('Error transferring unit:', error);
      toast.error(error.response?.data?.detail || 'Gagal pindah unit');
    } finally {
      setLoading(false);
    }
  };

  if (!open || !rental) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-gradient-to-br from-slate-800 to-slate-900 border-2 border-slate-700 rounded-2xl shadow-2xl w-full max-w-2xl mx-4">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-blue-600 px-6 py-4 flex items-center justify-between">
          <div>
            <h3 className="text-xl font-bold text-white">Pindah Unit</h3>
            <p className="text-sm text-purple-100">{rental.customer_name} - {rental.ps_unit_name}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg">
            <X className="h-5 w-5 text-white" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Transfer Type */}
          <div className="space-y-2">
            <label className="text-white font-semibold">Pilih Jenis Pindah:</label>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setTransferType('continue')}
                className={`p-4 rounded-xl border-2 transition ${
                  transferType === 'continue'
                    ? 'bg-blue-500/20 border-blue-500'
                    : 'bg-slate-800/50 border-slate-700 hover:border-slate-600'
                }`}
              >
                <p className="text-white font-bold">Lanjutkan Waktu</p>
                <p className="text-xs text-white/60 mt-1">Pindah biasa, waktu tidak reset</p>
              </button>
              
              <button
                onClick={() => setTransferType('change_mode')}
                className={`p-4 rounded-xl border-2 transition ${
                  transferType === 'change_mode'
                    ? 'bg-purple-500/20 border-purple-500'
                    : 'bg-slate-800/50 border-slate-700 hover:border-slate-600'
                }`}
              >
                <p className="text-white font-bold">Ganti Mode</p>
                <p className="text-xs text-white/60 mt-1">Reset waktu, ganti mode rental</p>
              </button>
            </div>
          </div>

          {/* Warning for change mode */}
          {transferType === 'change_mode' && (
            <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-xl">
              <div className="flex gap-2">
                <AlertTriangle className="h-5 w-5 text-yellow-400 flex-shrink-0" />
                <div>
                  <p className="text-yellow-200 font-bold text-sm">Ganti Mode Rental</p>
                  <p className="text-yellow-100/80 text-xs mt-1">
                    Waktu akan direset ke 0. Billing sebelumnya akan terpisah dari billing baru.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* TODO: Mode selection UI when change_mode */}
          {transferType === 'change_mode' && (
            <div className="p-4 bg-orange-500/10 border border-orange-500/30 rounded-xl">
              <p className="text-orange-200 text-sm">
                 Fitur ganti mode (pilih durasi/paket) masih dalam development.
                Untuk sementara, gunakan "Lanjutkan Waktu" saja.
              </p>
            </div>
          )}

          {/* Unit Selection */}
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <label className="text-white font-semibold">Pilih Unit Tujuan:</label>
              <p className="text-xs text-blue-400">Hanya {getCurrentType()}</p>
            </div>
            
            {units.length === 0 ? (
              <div className="text-center py-8 text-white/60">
                <p>Tidak ada unit {getCurrentType()} lain yang tersedia</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                {units.map((unit) => (
                  <button
                    key={unit.id}
                    onClick={() => setSelectedUnit(unit)}
                    className={`p-3 rounded-xl border-2 transition ${
                      selectedUnit?.id === unit.id
                        ? 'bg-green-500/20 border-green-500'
                        : 'bg-slate-800/50 border-slate-700 hover:border-slate-600'
                    }`}
                  >
                    <p className="text-white font-bold">{unit.name}</p>
                    <p className="text-xs text-white/60">{unit.branch}</p>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-slate-700 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-xl font-semibold"
            disabled={loading}
          >
            Batal
          </button>
          <button
            onClick={handleTransfer}
            className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-xl font-semibold disabled:opacity-50"
            disabled={!selectedUnit || loading || transferType === 'change_mode'}
          >
            {loading ? 'Memindahkan...' : 'Pindah Unit'}
          </button>
        </div>
      </div>
    </div>
  );
}
